import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCloudUploadAlt,
  FaTrash,
  FaTimes,
  FaSpinner,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import classNames from "classnames/bind";
import styles from "./WashUploadPage.module.css";
import { ToastContext } from "~/components/context/ToastContext";
import { ModalContext } from "~/components/context/ModalContext";
import { BASE_DRIVER_URL } from "~/helper/apiService";

const cx = classNames.bind(styles);

const WashUploadPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);

  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

  const fetchWashHistory = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_DRIVER_URL}driver-wash-history/${id}`, {
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 404) throw new Error("Không tìm thấy lịch sử rửa xe");
        throw new Error(`Lỗi HTTP! Status: ${response.status}`);
      }
      const data = await response.json();
      let updatedHistory = data.history || [];

      const currentMonthEntry = updatedHistory.find((entry) => entry.month === currentMonth);
      if (!currentMonthEntry) {
        updatedHistory = [
          {
            month: currentMonth,
            washCount: 0,
            requiredWash: 8,
            missingWash: 8,
            reports: [],
          },
          ...updatedHistory,
        ];
      }

      setHistory(updatedHistory);
      setExpandedMonth((prev) => prev || currentMonth);
    } catch (err) {
      showToast(err.message || "Lỗi khi tải lịch sử rửa xe", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWashHistory();
  }, [id]);

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files).slice(0, 1);
    if (selectedFiles.length === 0) {
      showToast("Vui lòng chọn đúng một ảnh", "error");
      return;
    }

    const maxSizeMB = 5;
    const validTypes = ["image/jpeg", "image/png", "image/gif"];
    for (const file of selectedFiles) {
      if (!validTypes.includes(file.type)) {
        showToast("Chỉ chấp nhận file JPEG, PNG hoặc GIF", "error");
        return;
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        showToast(`File ${file.name} vượt quá ${maxSizeMB}MB`, "error");
        return;
      }
    }

    const formData = new FormData();
    formData.append("vehicleId", id);
    selectedFiles.forEach((file) => formData.append("images", file));

    try {
      setUploading(true);
      const response = await fetch(`${BASE_DRIVER_URL}wash/${id}`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || `Lỗi HTTP! Status: ${response.status}`);
      }

      showToast("Tải ảnh rửa xe thành công, đang chờ duyệt", "success");
      await fetchWashHistory();
    } catch (err) {
      showToast(err.message || "Lỗi khi tải ảnh lên", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    try {
      const response = await fetch(`${BASE_DRIVER_URL}delete-report/${reportId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || `Lỗi HTTP! Status: ${response.status}`);
      }

      showToast("Xóa báo cáo thành công", "success");
      await fetchWashHistory();
      return true;
    } catch (err) {
      showToast(err.message || "Lỗi khi xóa báo cáo", "error");
      return false;
    }
  };

  const confirmDelete = (reportId) => {
    showModal({
      title: "Xác Nhận Xóa",
      message: `Bạn có chắc chắn muốn xóa báo cáo với ID ${reportId}?`,
      onConfirm: () => handleDeleteReport(reportId),
      onCancel: null,
    });
  };

  const toggleMonth = (month) => {
    setExpandedMonth((prev) => (prev === month ? null : month));
  };

  const getStatusText = (status) => {
    switch (status) {
      case "approved":
        return "Đã duyệt";
      case "pending":
        return "Chờ duyệt";
      case "rejected":
        return "Bị từ chối";
      default:
        return "Không xác định";
    }
  };

  const handleImageError = (e, reportId, idx) => {
    e.target.style.display = 'none';
    const parent = e.target.parentNode;
    const errorSpan = document.createElement('span');
    errorSpan.className = cx('image-error');
    errorSpan.textContent = 'Ảnh đã bị xóa bởi admin';
    parent.appendChild(errorSpan);
  };

  return (
    <div className={cx("container")}>
      <div className={cx("content")}>
        <div className={cx("header")}>
          <button
            className={cx("back-button")}
            onClick={() => navigate(-1)}
            aria-label="Quay lại"
          >
            <FaArrowLeft size="lg" />
          </button>
          <h2 className={cx("title")}>Rửa xe theo tháng</h2>
        </div>

        {loading ? (
          <p className={cx("loading")}>
            <FaSpinner className={cx("spinner")} />
            {" "}Đang tải dữ liệu...
          </p>
        ) : history.length === 0 ? (
          <p className={cx("no-data")}>Không có lịch sử rửa xe</p>
        ) : (
          <div className={cx("upload-container")}>
            {history.map((month) => {
              const isCurrentMonth = month.month === currentMonth;
              const isComplete = month.washCount >= 8;
              const canUpload = isCurrentMonth && month.washCount < 8;

              return (
                <div key={month.month} className={cx("month-section")}>
                  <div
                    className={cx("month-header")}
                    onClick={() => toggleMonth(month.month)}
                  >
                    <h3 className={cx("subtitle")}>
                      {new Date(month.month + "-01").toLocaleDateString("vi-VN", {
                        year: "numeric",
                        month: "long",
                      })}
                    </h3>
                    <span
                      className={cx(
                        "status",
                        isComplete ? "status-complete" : "status-pending"
                      )}
                    >
                      {isComplete
                        ? "✅ Hoàn thành"
                        : `⏳ ${month.washCount}/8 lần`}
                    </span>
                    <FaChevronUp
                      style={{ display: expandedMonth === month.month ? "inline" : "none" }}
                    />
                    <FaChevronDown
                      style={{ display: expandedMonth !== month.month ? "inline" : "none" }}
                    />
                  </div>

                  {expandedMonth === month.month && (
                    <div className={cx("month-content")}>
                      {isCurrentMonth && month.reports.length === 0 && canUpload ? (
                        <label className={cx("upload-button", { uploading })}>
                          <FaCloudUploadAlt size={48} />
                          <span className={cx("upload-text")}>
                            {uploading
                              ? "Đang tải..."
                              : `Chọn 1 ảnh cho lần rửa xe`}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className={cx("file-input")}
                            disabled={uploading || !canUpload}
                          />
                        </label>
                      ) : (
                        <table className={cx("table")}>
                          <thead>
                            <tr>
                              <th className={cx("th")}>Ảnh</th>
                              <th className={cx("th")}>Trạng thái</th>
                              <th className={cx("th")}>Thời gian</th>
                              <th className={cx("th")}>Hành động</th>
                            </tr>
                          </thead>
                          <tbody>
                            {month.reports.map((report) => (
                              <tr key={report.id} className={cx("tr")}>
                                <td className={cx("td")}>
                                  {report.images.length > 0 ? (
                                    report.images.map((img, idx) => (
                                      <div key={idx} className={cx("image-container")}>
                                        <img
                                          src={`https://xanh19.com/${img}`}
                                          alt={`Wash ${report.id}-${idx}`}
                                          onClick={() =>
                                            setSelectedImage({
                                              url: `https://xanh19.com/${img}`,
                                              reportId: report.id,
                                            })
                                          }
                                          onError={(e) => handleImageError(e, report.id, idx)}
                                        />
                                      </div>
                                    ))
                                  ) : (
                                    <span>Không có ảnh</span>
                                  )}
                                </td>
                                <td
                                  className={cx("td", {
                                    "status-approved": report.status === "approved",
                                    "status-pending": report.status === "pending",
                                    "status-rejected": report.status === "rejected",
                                  })}
                                >
                                  {getStatusText(report.status)}
                                </td>
                                <td className={cx("td")}>
                                  {new Date(report.created_at).toLocaleString("vi-VN", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                  })}
                                </td>
                                <td className={cx("td")}>
                                    <div className={cx("button-container")}>
                                      <button
                                        className={cx("delete-button")}
                                        onClick={() => confirmDelete(report.id)}
                                        aria-label="Xóa báo cáo"
                                      >
                                        <FaTrash />
                                      </button>
                                    </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {canUpload && (
                        <label className={cx("upload-button-small", { uploading })}>
                          <FaCloudUploadAlt size={24} />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className={cx("file-input")}
                            disabled={uploading || !canUpload}
                          />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {selectedImage && (
          <div className={cx("modal-overlay", { active: selectedImage })}>
            <div className={cx("modal-content")} onClick={(e) => e.stopPropagation()}>
              <button
                className={cx("close-button")}
                onClick={() => setSelectedImage(null)}
                aria-label="Đóng"
              >
                <FaTimes size="lg" />
              </button>
              <img
                src={selectedImage.url}
                alt={`Enlarged wash ${selectedImage.reportId}`}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const errorSpan = document.createElement('span');
                  errorSpan.className = cx('image-error');
                  errorSpan.textContent = 'Ảnh đã bị xóa bởi admin';
                  e.target.parentNode.appendChild(errorSpan);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WashUploadPage;